import { Component, OnInit, OnDestroy, model, signal } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { filter, Subject, takeUntil } from 'rxjs';
import { FooterComponent } from './components/footer/footer.component';
import { HeaderComponent } from './components/header/header.component';
import { SideNavigationComponent } from './components/side-navigation/side-navigation.component';
import { BootstrapService } from '../../core/services/bootstrap.service';
import { DropdownPortalService } from '../../core/services/dropdown-portal.service';

@Component({
  selector: 'app-dashboard.layout',
  imports: [SideNavigationComponent, FooterComponent, HeaderComponent, RouterOutlet],
  templateUrl: './dashboard.layout.html',
})
export class DashboardLayout  implements OnInit, OnDestroy {
  public isSidebarCollapsed = model<boolean>(false);
  public isScrolled = false;
  private destroy$ = new Subject<void>();
  public isActiveUserLoaded = signal<boolean>(false);

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private bootstrapService: BootstrapService,
    private dropdownPortalService: DropdownPortalService,
  ) {}

  public toggleSidebar(): void {
    this.isSidebarCollapsed.set(!this.isSidebarCollapsed());
  }

  public ngOnInit(): void {
    // Set up responsive sidebar behavior using BreakpointObserver
    this.setupResponsiveSidebar();

    // Listen for route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Sweep any modal backdrop the just-unmounted page left behind (e.g. browser Back
        // dismissed the page without running Bootstrap's modal.hide lifecycle). Runs on
        // NavigationEnd only, so a cancelled CanDeactivate keeps the original modal intact.
        this.bootstrapService.cleanupOrphanedModals();
        // Same for an Actions menu the page left portaled on <body>.
        this.dropdownPortalService.releaseOrphaned();
        // Auto-close sidebar on mobile after navigation
        const isMobile = this.breakpointObserver.isMatched('(max-width: 991.98px)');
        if (isMobile && !this.isSidebarCollapsed()) {
          this.isSidebarCollapsed.set(true);
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupResponsiveSidebar(): void {
    this.breakpointObserver
      .observe(['(max-width: 991.98px)']) // Bootstrap's lg breakpoint
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: BreakpointState) => {
        if (result.matches) {
          // Screen is smaller than lg (< 992px) - always force collapse sidebar
          this.isSidebarCollapsed.set(true);
        } else {
          // Screen is lg or larger (>= 992px) - expand sidebar
          this.isSidebarCollapsed.set(false);
        }
      });
  }
}
